import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAllCountriesStart, getRegistrationFieldsStart, getTenantDetailsStart, userSignUpStart } from '../../../store/redux-slices/login'
import { sendStrictPhoneOTPStart } from '../../../store/redux-slices/user'
import { Buffer } from 'buffer'
import { formatDateMDY } from '../../../utils/dateFormatter'
import { useNavigate, useParams } from 'react-router-dom'
import { getItem, getLoginToken } from '../../../utils/storageUtils'
import { browserFingerPrint } from '../../../utils/browserFingerprint'
import { getAffiliateCodeForPayload } from 'utils/signup'
import { PHONE_VERIFICATION_TYPE } from 'constants/general'

const useSignUp = () => {
  const checkKycWithSignup = process.env.REACT_APP_KYC_WITH_SIGNUP === 'true'
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { languageCode } = useParams()
  const { regFields, tenantDetails, allCountries, loading } = useSelector((state) => state.login)
  const { languages, languageData, selectedLanguageCode, userIPCountry, phoneVerificationType } = useSelector((state) => state.language)
  const generalConfig = useSelector((state) => state.uiConfig?.general)
  const [toggleViewPassword, setToggleViewPassword] = useState({
    password: false,
    confirmPassword: false
  })
  const [search, setSearch] = useState('')
  const [language, setLanguage] = useState(languageCode || selectedLanguageCode?.toLowerCase() || getItem('language')?.toLowerCase())
  const [disableSubmit, setDisableSubmit] = useState(false)

  // For Privacy and Policy
  useEffect(() => {
    const langCode = language?.toLowerCase()
    const anchor = document?.getElementById('privacy-and-policy')?.querySelector('a')
    const href = anchor?.getAttribute('href')
    const newHref = href?.replace(/\/[a-z][a-z]\//, `/${langCode}/`)
    if (anchor) anchor.href = newHref
  }, [language, languageData])

  // For Terms and Conditions
  useEffect(() => {
    const langCode = language?.toLowerCase()
    const anchor = document?.getElementById('terms-and-conditions')?.querySelector('a')
    const href = anchor?.getAttribute('href')
    const newHref = href?.replace(/\/[a-z][a-z]\//, `/${langCode}/`)
    if (anchor) anchor.href = newHref
  }, [language, languageData])

  // Taking only the true registration fields
  const arr =
    regFields &&
    Object.keys(regFields)
      .map((keyName, i) => regFields[keyName] !== false && keyName)
      .filter((ele) => ele !== false && ele)

  // Converting registration fields array into object with empty values
  const initialState =
    arr &&
    arr.reduce((accumulator, currentValue) => {
      accumulator[currentValue] = ''
      return accumulator
    }, {})

  const handleInitialState = (formValues) => {
    const { recaptchaToken } = formValues
    for (const k in initialState) {
      if (k === 'newsLetter' || k === 'sms' || k === 'newsletterAndSms') {
        initialState[k] = formValues[k] ? 'true' : 'false'
      } else {
        initialState[k] = formValues[k]
      }
    }
    if (regFields.phone) {
      initialState.phoneCode = formValues.phoneCode
    }
    if (regFields.address) {
      initialState.city = formValues.city.trim()
      initialState.zipCode = formValues.zipCode.trim()
    }
    for (const k in initialState) {
      if (regFields[k] === 0 ||
       initialState[k] === '' || initialState[k] === null) {
        delete initialState[k] // Deleting the fields which are empty/null
        if (k === 'newsLetter') initialState.newsLetter = 'false'
        if (k === 'sms') initialState.sms = 'false'
      }
    }
    const encryptedPass = Buffer.from(initialState.password).toString('base64')
    initialState.password = encryptedPass
    initialState.confirmPassword && (initialState.confirmPassword = encryptedPass)
    initialState.dateOfBirth && (initialState.dateOfBirth = formatDateMDY(initialState.dateOfBirth))
    initialState.uniqueKey = browserFingerPrint()

    if (initialState.affiliateCode) {
      if (initialState.token) {
        delete initialState.token
      }
    } else {
      const refToken = getAffiliateCodeForPayload()
      if (refToken) {
        initialState.token = refToken
      }
      delete initialState.affiliateCode
    }

    if (recaptchaToken) {
      initialState.recaptchaToken = recaptchaToken
    }

    if (phoneVerificationType === PHONE_VERIFICATION_TYPE.STRICT) {
      dispatch(
        sendStrictPhoneOTPStart({
          phone: initialState.phone,
          phoneCode: initialState.phoneCode,
          signupData: initialState,
          setDisableSubmit
        })
      )
    } else {
      userSignUp(initialState)
    }
  }

  useEffect(() => {
    if (getLoginToken()) {
      navigate('/')
    }
  }, [])

  useEffect(() => {
    dispatch(getRegistrationFieldsStart())
    dispatch(getTenantDetailsStart())
  }, [])

  useEffect(() => {
    dispatch(getAllCountriesStart())
  }, [selectedLanguageCode])

  const userSignUp = (initialState) => dispatch(userSignUpStart({ initialState, navigate, returnTo: window.locationPath, setDisableSubmit, checkKycWithSignup, kycFailedErrorMsg: languageData?.kycOnSignupFailed || 'Signup failed, please reach out to support' }))

  // For required * sign
  const showStar = (fieldName) =>
    fieldName === 2 &&
      <span className='text-danger'>&nbsp;*&nbsp;</span>

  return {
    loading,
    regFields,
    toggleViewPassword,
    tenantDetails,
    generalConfig,
    allCountries,
    languageData,
    language,
    selectedLanguageCode,
    languageCode,
    languages,
    setLanguage,
    navigate,
    setToggleViewPassword,
    handleInitialState,
    showStar,
    search,
    setSearch,
    countryCode: userIPCountry,
    disableSubmit,
    setDisableSubmit
  }
}

export default useSignUp
